import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import LinkIcon from "@mui/icons-material/Link";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	FormControlLabel,
	Grid2,
	Switch,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { FormikProps } from "formik";
import React, { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
	useBusinessDomains,
	useJobLocations,
	useJobTypes,
	useSectors,
	useSkills
} from "../../../../../hooks/useReferential";
import { BusinessDomain } from "../../../../../models/BusinessDomain";
import { FreelancerProfile } from "../../../../../models/FreelancerProfile";
import { FreelancerProfileProfessionalExperienceBusinessDomain } from "../../../../../models/FreelancerProfileProfessionalExperienceBusinessDomain";
import { FreelancerProfileProfessionalExperienceDocument } from "../../../../../models/FreelancerProfileProfessionalExperienceDocument";
import { FreelancerProfileProfessionalExperienceSkill } from "../../../../../models/FreelancerProfileProfessionalExperienceSkill";
import { FreelancerProfileProfessionalExperienceUrl } from "../../../../../models/FreelancerProfileProfessionalExperienceUrl";
import { Skill } from "../../../../../models/Skill";
import { zoullooColors } from "../../../../../theme";
import { convertPlaceToCity } from "../../../../../utils/placesUtils";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import HtmlEditor from "../../../../UI/HtmlEditor";
import PlacesAutocomplete from "../../../../UI/PlacesAutocomplete";
import DocumentsTable from "./DocumentsTable";
import UrlsTable from "./UrlsTable";
import { useTranslatableForm } from "../../../../../hooks/useTranslatableForm";
import { LanguageCode } from "../../../../../models/enums/LanguageCode";
import TranslatableFieldWrapper from "../../../../translation/TranslatableFieldWrapper";

interface ExperienceFormProps {
	readonly formik: FormikProps<FreelancerOnboardingMyPresentationStepViewModel>;
	readonly freelancerProfile: FreelancerProfile;
	readonly experience: FreelancerOnboardingMyPresentationExperienceViewModel;
	readonly index: number;
	readonly onConfirm: (index: number) => void;
	readonly onCancel: (index: number) => void;
	readonly getTextFieldHandlers: (fieldPath: string) => {
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
		onBlur: () => void;
	};
	readonly getAutocompleteHandlers: (fieldPath: string) => {
		onChange: (event: any, value: any) => void;
		onBlur: () => void;
	};
	readonly getCheckboxHandlers: (fieldPath: string) => {
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
		onBlur: () => void;
	};
	readonly getDatePickerHandlers: (fieldPath: string) => {
		onChange: (newValue: any) => void;
	};
	readonly getDebouncedUpdate: (fieldPath: string) => (value: string) => void;
	readonly hasFieldError: (fieldPath: string) => boolean;
	readonly getFieldError: (fieldPath: string) => string | undefined;
	readonly formikRef: React.MutableRefObject<
		FormikProps<FreelancerOnboardingMyPresentationStepViewModel>
	>;
	readonly setErrorDialog: Dispatch<
		SetStateAction<{
			open: boolean;
			title: string;
			message: string;
		}>
	>;
	readonly disableConfirmButtonOnPendingSave: boolean;
	readonly setIsCityValueChangedOnLoadBecauseOfBeingTranslated: Dispatch<
		SetStateAction<boolean>
	>;
	readonly onTranslatableFormRef?: (ref: ReturnType<typeof useTranslatableForm> | null) => void;
}

export default function ExperienceForm({
	formik,
	freelancerProfile,
	experience,
	index,
	onConfirm,
	onCancel,
	getTextFieldHandlers,
	getAutocompleteHandlers,
	getCheckboxHandlers,
	getDatePickerHandlers,
	getDebouncedUpdate,
	hasFieldError,
	getFieldError,
	formikRef,
	setErrorDialog,
	disableConfirmButtonOnPendingSave,
	setIsCityValueChangedOnLoadBecauseOfBeingTranslated,
	onTranslatableFormRef
}: ExperienceFormProps) {
	const { t, i18n } = useTranslation();

	// Translatable form for the 4 translatable fields
	const translatableForm = useTranslatableForm({
		entityType: "FreelancerProfileProfessionalExperience",
		entityId: experience.id,
		// Anchor the source on the TALENT's input language (settings-anchored rules: source chip,
		// AI-first selector gate, no hand-back on the source).
		sourceLanguage: freelancerProfile?.freelancer?.user?.settings?.preferredLanguage,
		translatableFields: ["jobTitle", "missionDescription", "results", "technicalStack"],
		initialValues: {
			jobTitle: experience.jobTitle ?? "",
			missionDescription: experience.missionDescription ?? "",
			results: experience.results ?? "",
			technicalStack: experience.technicalStack ?? "",
		},
	});

	// Expose translatableForm to parent for EN save via a stable ref wrapper
	const translatableFormRef = useRef(translatableForm);
	translatableFormRef.current = translatableForm;

	// Create a stable proxy that delegates to the ref (never changes identity)
	const [stableProxy] = React.useState(() => ({
		getModifiedLanguages: () => translatableFormRef.current.getModifiedLanguages(),
		getFieldValue: (field: string) => translatableFormRef.current.getFieldValue(field),
		getPrimaryValues: () => translatableFormRef.current.getPrimaryValues(),
		get editingLanguage() { return translatableFormRef.current.editingLanguage; },
		switchLanguage: (...args: any[]) => (translatableFormRef.current.switchLanguage as any)(...args),
		setFieldValue: (...args: any[]) => (translatableFormRef.current.setFieldValue as any)(...args),
		// MyPresentation reads this on the shared ref to decide if an experience field is filled in
		// any language — it must be on the proxy, or the validation throws "not a function".
		hasValueInAnyLanguage: (field: string) => translatableFormRef.current.hasValueInAnyLanguage(field),
		// Forward EVERY remaining hook member: the `as ReturnType<...>` cast below hides a missing
		// one from tsc, so a consumer reaching it through the shared ref only crashes at runtime
		// (exactly how hasValueInAnyLanguage broke before being added here).
		isFieldStale: (field: string) => translatableFormRef.current.isFieldStale(field),
		get canAutoTranslate() { return translatableFormRef.current.canAutoTranslate; },
		isFieldAiManaged: (field: string) => translatableFormRef.current.isFieldAiManaged(field),
		isFieldHandBackable: (field: string) => translatableFormRef.current.isFieldHandBackable(field),
		isFieldRegenerating: (field: string) => translatableFormRef.current.isFieldRegenerating(field),
		enableAiForField: (field: string) => translatableFormRef.current.enableAiForField(field),
		// The save flows (ExperienceListContainer confirm + MyPresentation floating save) read these
		// on the shared ref to build per-language payloads and clear the tracking after a save.
		getValuesForLanguage: (lang: LanguageCode) =>
			translatableFormRef.current.getValuesForLanguage(lang),
		markLanguageSaved: (lang: LanguageCode) =>
			translatableFormRef.current.markLanguageSaved(lang),
		get translationsLoaded() { return translatableFormRef.current.translationsLoaded; },
		getFieldContentToken: (field: string) =>
			translatableFormRef.current.getFieldContentToken(field),
		get sourceLanguage() { return translatableFormRef.current.sourceLanguage; },
		getFieldUiState: (field: string) => translatableFormRef.current.getFieldUiState(field),
		isFieldReadOnly: (field: string) => translatableFormRef.current.isFieldReadOnly(field),
		isLanguageSelectorVisible: (field: string) =>
			translatableFormRef.current.isLanguageSelectorVisible(field),
		isAiFirstNoteVisible: (field: string) =>
			translatableFormRef.current.isAiFirstNoteVisible(field),
		isFieldDraft: (field: string) => translatableFormRef.current.isFieldDraft(field),
		beginFieldDraft: (field: string) => translatableFormRef.current.beginFieldDraft(field),
		cancelFieldDraft: (field: string) => translatableFormRef.current.cancelFieldDraft(field),
	} as ReturnType<typeof useTranslatableForm>));

	React.useEffect(() => {
		onTranslatableFormRef?.(stableProxy);
		return () => {
			// Don't drop the entry when the form unmounts (the experience is merely collapsed):
			// freeze the "filled in any language" answers so the page-level Yup validation still
			// passes for an EN-only experience whose FR column is empty. Edits are already persisted
			// on confirm, so the frozen entry reports no modified languages (no redundant re-save).
			const fields = ["jobTitle", "missionDescription", "results", "technicalStack"];
			const filled = fields.reduce<Record<string, boolean>>((acc, field) => {
				acc[field] = translatableFormRef.current.hasValueInAnyLanguage(field);
				return acc;
			}, {});
			// Only the two members the parent reads on collapsed entries (validation +
			// floating-save) are needed; cast through unknown since this is a deliberate partial.
			onTranslatableFormRef?.({
				getModifiedLanguages: () => [],
				hasValueInAnyLanguage: (field: string) => filled[field] ?? false,
			} as unknown as ReturnType<typeof useTranslatableForm>);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	// Shared TranslatableFieldWrapper props for one translatable field: status chip state,
	// AI-first selector gate and the explicit unlock (draft) flow.
	const translatableWrapperProps = (field: string, formikPath: string) => ({
		editingLanguage: translatableForm.editingLanguage,
		onLanguageChange: translatableForm.switchLanguage,
		isStale: translatableForm.isFieldStale(field),
		uiState: translatableForm.getFieldUiState(field),
		sourceLanguage: translatableForm.sourceLanguage,
		showLanguageSelector: translatableForm.isLanguageSelectorVisible(field),
		showAiFirstNote: translatableForm.isAiFirstNoteVisible(field),
		isDraft: translatableForm.isFieldDraft(field),
		onBeginDraft: () => translatableForm.beginFieldDraft(field),
		onCancelDraft: () => {
			const restored = translatableForm.cancelFieldDraft(field);
			// Formik mirrors the FR value live while the draft is being typed: resync it on cancel,
			// or the abandoned draft would still be persisted by the entity save.
			if (translatableForm.editingLanguage === LanguageCode.Fr) {
				getDebouncedUpdate(formikPath)(restored);
			}
		},
		canHandBackToAi: translatableForm.isFieldHandBackable(field),
		isRegenerating: translatableForm.isFieldRegenerating(field),
		onEnableAi: () => translatableForm.enableAiForField(field),
		contentReadyToken: translatableForm.getFieldContentToken(field),
	});

	const { data: sectors } = useSectors();
	const { data: jobTypes } = useJobTypes();
	const { data: jobLocations } = useJobLocations();
	const { data: skills } = useSkills();
	const { data: businessDomains } = useBusinessDomains();

	// Shared styles to avoid duplication
	const datePickerStyles: SxProps<Theme> = {
		width: "100%",
		"& .MuiInputBase-root": {
			borderRadius: "12px",
			backgroundColor: "#fafafa",
			transition: "all 0.3s ease",
			"&:hover": {
				backgroundColor: "#f5f5f5"
			},
			"&.Mui-focused": {
				backgroundColor: "#ffffff",
				boxShadow: `0 0 0 2px ${zoullooColors.secondary.main}25`
			}
		},
		"& .MuiOutlinedInput-root": {
			borderRadius: "12px",
			"& fieldset": {
				borderColor: zoullooColors.grey[300],
				borderWidth: "1.5px"
			},
			"&:hover fieldset": {
				borderColor: zoullooColors.secondary.main,
				borderWidth: "2px"
			},
			"&.Mui-focused fieldset": {
				borderColor: zoullooColors.secondary.main,
				borderWidth: "2px"
			}
		},
		"& .MuiInputLabel-root": {
			color: zoullooColors.grey[600],
			fontWeight: 500,
			"&.Mui-focused": {
				color: zoullooColors.secondary.main
			}
		}
	};

	// Helper function to create tooltip labels
	const createTooltipLabel = (labelKey: string, tooltipKey: string): JSX.Element => (
		<Box sx={{ marginTop: "4px" }}>
			{t(labelKey)}
			<Tooltip
				title={
					<Typography variant="body2">
						<Trans
							i18nKey={tooltipKey}
							components={{
								br: <br />
							}}
						/>
					</Typography>
				}
				arrow
				slotProps={{
					tooltip: {
						sx: { maxWidth: "500px !important" }
					}
				}}
			>
				<HelpOutlineSharpIcon
					fontSize="medium"
					sx={{
						ml: 1,
						mb: 0.5,
						color: zoullooColors.grey[400],
						verticalAlign: "middle",
						cursor: "help"
					}}
				/>
			</Tooltip>
		</Box>
	);

	// State to coordinate document and URL table visibility (only one can be shown at a time)
	const [activeTable, setActiveTable] = useState<"documents" | "urls" | null>(null);
	const [isAddingUrl, setIsAddingUrl] = useState(false);
	const [addingUrl, setAddingUrl] =
		useState<FreelancerProfileProfessionalExperienceUrl | null>(null);
	const [isAddingDocument, setIsAddingDocument] = useState(false);
	const [addingDocument, setAddingDocument] =
		useState<FreelancerProfileProfessionalExperienceDocument | null>(null);

	// Refs for scrolling to error fields
	const fieldRefs = useRef<{ [key: string]: HTMLElement | null }>({});

	// Helper function to set field refs
	const setFieldRef = useCallback((fieldName: string, element: HTMLElement | null) => {
		fieldRefs.current[fieldName] = element;
	}, []);

	// Helper function to scroll to an element with offset
	const scrollToElement = useCallback((element: HTMLElement) => {
		const elementRect = element.getBoundingClientRect();
		const scrollTop = elementRect.top + window.pageYOffset - 300; // 300px offset for better visibility

		window.scrollTo({
			top: scrollTop,
			behavior: "smooth"
		});
	}, []);

	// Helper function to focus input elements within a container
	const focusInputInElement = useCallback((element: HTMLElement) => {
		const focusableSelectors = [
			".ProseMirror", // HTML editors
			"input[type='text']", // Text inputs
			"input:not([type='hidden'])", // All visible inputs
			"textarea",
			"[contenteditable]",
			".MuiInputBase-input", // Material-UI input
			".MuiAutocomplete-input", // Autocomplete specific input
			"[role='combobox']" // Autocomplete combobox role
		];

		for (const selector of focusableSelectors) {
			const input = element.querySelector(selector) as HTMLElement;

			if (input && typeof input.focus === "function") {
				// For MUI Autocomplete, we need to trigger the click to open the dropdown
				if (input.closest(".MuiAutocomplete-root")) {
					const autocompleteRoot = input.closest(".MuiAutocomplete-root") as HTMLElement;
					const endAdornment = autocompleteRoot.querySelector(
						".MuiAutocomplete-endAdornment"
					) as HTMLElement;

					// Click the dropdown arrow to open the autocomplete
					if (endAdornment) {
						endAdornment.click();
					}
				}

				input.focus();

				// Select text for input/textarea elements
				if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
					input.select?.();
				}

				return true; // Successfully focused
			}
		}

		return false; // No focusable element found
	}, []);

	// Helper function to scroll to first error
	const scrollToFirstError = useCallback(async () => {
		// Get the current validation errors
		const validationErrors = await formikRef.current.validateForm();

		// Get all error field paths and sort them by the order they appear in the form
		const fieldOrder = [
			`professionalExperiences.${index}.companyName`,
			`professionalExperiences.${index}.sector`,
			`professionalExperiences.${index}.jobTitle`,
			`professionalExperiences.${index}.startDate`,
			`professionalExperiences.${index}.endDate`,
			`professionalExperiences.${index}.city`,
			`professionalExperiences.${index}.missionDescription`,
			`professionalExperiences.${index}.results`,
			`professionalExperiences.${index}.technicalStack`
		];

		// Helper function to check if a field path has an error in the validation errors object
		const hasValidationError = (fieldPath: string, errors: any): boolean => {
			const pathParts = fieldPath.split(".");
			let current = errors;

			for (const part of pathParts) {
				if (!current || typeof current !== "object") return false;
				current = current[part];
			}

			return typeof current === "string" && current.length > 0;
		};

		// Find the first error field in order
		const errorField = fieldOrder.find(fieldPath => {
			const element = fieldRefs.current[fieldPath];
			return element && hasValidationError(fieldPath, validationErrors);
		});

		if (!errorField) {
			return;
		}

		const element = fieldRefs.current[errorField];
		if (!element) {
			return;
		}

		// Scroll to the error field
		scrollToElement(element);

		// Focus the input after a short delay to ensure smooth scrolling completes
		setTimeout(() => {
			try {
				focusInputInElement(element);
			} catch (error) {
				console.debug("Could not focus input element:", error);
			}
		}, 500);
	}, [index, scrollToElement, focusInputInElement, formikRef]);

	const handleConfirm = useCallback(
		async (index: number) => {
			try {
				// Validate the form and get any errors
				const validationErrors = await formikRef.current.validateForm();

				const hasValidationErrors = validationErrors.professionalExperiences?.[index];

				if (hasValidationErrors) {
					// Dynamically create touched state for fields with errors
					const touchedFields: { [key: string]: boolean } = {};

					// Helper function to recursively mark error fields as touched
					const markErrorFieldsAsTouched = (errors: any, prefix = "") => {
						Object.keys(errors).forEach(key => {
							const fieldPath = prefix ? `${prefix}.${key}` : key;

							if (typeof errors[key] === "object" && !Array.isArray(errors[key])) {
								// Nested object - recurse deeper
								markErrorFieldsAsTouched(errors[key], fieldPath);
							} else {
								// This is an actual error - mark the field as touched
								touchedFields[fieldPath] = true;
							}
						});
					};

					markErrorFieldsAsTouched(validationErrors);

					// Set only the fields with errors as touched and wait for the state update
					await new Promise<void>(resolve => {
						formikRef.current.setTouched(
							{
								...formikRef.current.touched,
								...touchedFields
							},
							true // shouldValidate = true
						);

						// Use setTimeout to wait for the state update to complete
						setTimeout(() => {
							resolve();
						}, 150);
					});

					// Now scroll to the first error after the state has been updated
					await scrollToFirstError();

					return;
				}

				// No validation errors - proceed with confirmation
				onConfirm(index);
			} catch (error) {
				console.error("Error during form validation:", error);
			}
		},
		[formikRef, index, scrollToFirstError, onConfirm]
	);

	const handleCancel = useCallback(() => {
		onCancel(index);
	}, [onCancel, index]);

	// Use shared state instead of local state
	const showingUrlTable = activeTable === "urls";

	const addUrl = useCallback(() => {
		if (showingUrlTable) {
			// If currently showing URL table, hide it
			setActiveTable(null);
			setIsAddingUrl(false);
			setAddingUrl(null);
		} else {
			// If not showing URL table, show it (and this will hide document table if it's shown)
			setActiveTable("urls");
		}
	}, [showingUrlTable, setActiveTable]);

	const addDocument = useCallback(() => {
		if (activeTable === "documents") {
			// If currently showing document table, hide it
			setActiveTable(null);
			setIsAddingDocument(false);
			setAddingDocument(null);
		} else {
			// If not showing document table, show it (and this will hide URL table if it's shown)
			setActiveTable("documents");
		}
	}, [activeTable, setActiveTable]);

	return (
		<LocalizationProvider
			dateAdapter={AdapterDayjs}
			adapterLocale={i18n.language === "fr" ? "fr" : "en"}
		>
			<Card key={experience.id ?? index} sx={{ marginBottom: 3 }}>
				<CardHeader
					subheader={
						<FormControlLabel
							control={
								<Checkbox
									name={`professionalExperiences.${index}.isTopExperience`}
									checked={experience.isTopExperience}
									onChange={
										getCheckboxHandlers(
											`professionalExperiences.${index}.isTopExperience`
										).onChange
									}
									onBlur={
										getCheckboxHandlers(
											`professionalExperiences.${index}.isTopExperience`
										).onBlur
									}
									icon={<StarOutlineIcon />}
									checkedIcon={<StarIcon />}
								/>
							}
							label={createTooltipLabel(
								"freelancerOnboardingMyPresentation.experiences.topExperience",
								"freelancerOnboardingMyPresentation.experiences.topExperienceTooltip"
							)}
							labelPlacement="end"
						/>
					}
				/>
				<CardContent style={{ paddingTop: 0 }}>
					<Grid2 container spacing={3}>
						{/* Company Name */}
						<Grid2
							size={{ xs: 12, md: 6 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.companyName`, el)}
						>
							<TextField
								fullWidth
								label={t("freelancerOnboardingMyPresentation.experiences.companyName")}
								name={`professionalExperiences.${index}.companyName`}
								defaultValue={experience.companyName}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
									getDebouncedUpdate(`professionalExperiences.${index}.companyName`)(
										event.target.value
									)
								}
								onBlur={
									getTextFieldHandlers(`professionalExperiences.${index}.companyName`)
										.onBlur
								}
								error={hasFieldError(`professionalExperiences.${index}.companyName`)}
								helperText={getFieldError(`professionalExperiences.${index}.companyName`)}
							/>
						</Grid2>

						{/* Sector */}
						<Grid2
							size={{ xs: 12, md: 6 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.sector`, el)}
						>
							<Autocomplete
								options={sectors || []}
								getOptionLabel={option => t(`Sectors.${option.label}`)}
								value={experience.sector}
								onChange={(_, newValue) => {
									getAutocompleteHandlers(
										`professionalExperiences.${index}.sector`
									).onChange(_, newValue);
								}}
								onBlur={
									getAutocompleteHandlers(`professionalExperiences.${index}.sector`)
										.onBlur
								}
								renderInput={params => (
									<TextField
										{...params}
										label={t("freelancerOnboardingMyPresentation.experiences.sector")}
										fullWidth
										error={hasFieldError(`professionalExperiences.${index}.sector`)}
										helperText={getFieldError(`professionalExperiences.${index}.sector`)}
									/>
								)}
							/>
						</Grid2>

						{/* Job Title */}
						<Grid2
							size={{ xs: 12, md: 6 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.jobTitle`, el)}
						>
							<TranslatableFieldWrapper
								label={t("freelancerOnboardingMyPresentation.experiences.jobTitle")}
								{...translatableWrapperProps(
									"jobTitle",
									`professionalExperiences.${index}.jobTitle`
								)}
							>
								<TextField
									key={translatableForm.editingLanguage + "-jobTitle"}
									fullWidth
									name={`professionalExperiences.${index}.jobTitle`}
									// Native read-only carries the AI lock (the wrapper only dims): text stays
									// selectable/copiable and accessible, unlike the old pointer-events tarp.
									InputProps={{ readOnly: translatableForm.isFieldReadOnly("jobTitle") }}
									defaultValue={translatableForm.getFieldValue("jobTitle")}
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										translatableForm.setFieldValue("jobTitle", event.target.value);
										if (translatableForm.editingLanguage === LanguageCode.Fr) {
											getDebouncedUpdate(`professionalExperiences.${index}.jobTitle`)(
												event.target.value
											);
										}
									}}
									onBlur={
										getTextFieldHandlers(`professionalExperiences.${index}.jobTitle`).onBlur
									}
									error={hasFieldError(`professionalExperiences.${index}.jobTitle`)}
									helperText={getFieldError(`professionalExperiences.${index}.jobTitle`)}
								/>
							</TranslatableFieldWrapper>
						</Grid2>

						{/* Job Type */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Autocomplete
								options={jobTypes || []}
								getOptionLabel={option => t(`JobTypes.${option.label}`)}
								value={experience.jobType}
								onChange={
									getAutocompleteHandlers(`professionalExperiences.${index}.jobType`)
										.onChange
								}
								renderInput={params => (
									<TextField
										{...params}
										label={t("freelancerOnboardingMyPresentation.experiences.jobType")}
										placeholder={t(
											"freelancerOnboardingMyPresentation.experiences.selectJobType"
										)}
										fullWidth
									/>
								)}
							/>
						</Grid2>

						{/* Start Date */}
						<Grid2
							size={{ xs: 12, md: 4 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.startDate`, el)}
						>
							<DatePicker
								label={t("freelancerOnboardingMyPresentation.experiences.startDate")}
								value={experience.startDate ? dayjs(experience.startDate) : null}
								onChange={
									getDatePickerHandlers(`professionalExperiences.${index}.startDate`)
										.onChange
								}
								views={["year", "month"]}
								openTo="month"
								format="MM/YYYY"
								slotProps={{
									textField: {
										fullWidth: true,
										error: hasFieldError(`professionalExperiences.${index}.startDate`),
										helperText: getFieldError(
											`professionalExperiences.${index}.startDate`
										),
										onBlur: () => {
											formikRef.current.setFieldTouched(
												`professionalExperiences.${index}.startDate`,
												true
											);
										},
										sx: { backgroundColor: zoullooColors.background.default }
									},
									yearButton: {
										sx: {
											"&.Mui-selected": {
												backgroundColor: `${zoullooColors.secondary.main} !important`,
												color: "#ffffff !important",
												"&:hover": {
													backgroundColor: `${
														zoullooColors.secondary.dark || zoullooColors.secondary.main
													} !important`
												}
											}
										}
									},
									monthButton: {
										sx: {
											"&.Mui-selected": {
												backgroundColor: `${zoullooColors.secondary.main} !important`,
												color: "#ffffff !important",
												"&:hover": {
													backgroundColor: `${
														zoullooColors.secondary.dark || zoullooColors.secondary.main
													} !important`
												}
											}
										}
									}
								}}
								sx={datePickerStyles}
							/>
						</Grid2>

						{/* End Date */}
						<Grid2
							size={{ xs: 12, md: 4 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.endDate`, el)}
						>
							<DatePicker
								label={t("freelancerOnboardingMyPresentation.experiences.endDate")}
								value={experience.endDate ? dayjs(experience.endDate) : null}
								onChange={
									getDatePickerHandlers(`professionalExperiences.${index}.endDate`)
										.onChange
								}
								views={["year", "month"]}
								openTo="month"
								format="MM/YYYY"
								disabled={experience.isCurrentPosition}
								minDate={
									experience.startDate
										? dayjs(experience.startDate).add(1, "month")
										: undefined
								}
								slotProps={{
									textField: {
										fullWidth: true,
										error:
											!experience.isCurrentPosition &&
											hasFieldError(`professionalExperiences.${index}.endDate`),
										helperText: !experience.isCurrentPosition
											? getFieldError(`professionalExperiences.${index}.endDate`)
											: "",
										color: "secondary",
										onChange: getDatePickerHandlers(
											`professionalExperiences.${index}.endDate`
										).onChange,
										onBlur: () => {
											formikRef.current.setFieldTouched(
												`professionalExperiences.${index}.endDate`,
												true
											);
										},
										sx: { backgroundColor: zoullooColors.background.default }
									},
									yearButton: {
										sx: {
											"&.Mui-selected": {
												backgroundColor: `${zoullooColors.secondary.main} !important`,
												color: "#ffffff !important",
												"&:hover": {
													backgroundColor: `${
														zoullooColors.secondary.dark || zoullooColors.secondary.main
													} !important`
												}
											},
											"&.Mui-disabled": {
												backgroundColor: "#f5f5f5 !important",
												color: "#bdbdbd !important",
												cursor: "not-allowed !important"
											}
										}
									},
									monthButton: {
										sx: {
											"&.Mui-selected": {
												backgroundColor: `${zoullooColors.secondary.main} !important`,
												color: "#ffffff !important",
												"&:hover": {
													backgroundColor: `${
														zoullooColors.secondary.dark || zoullooColors.secondary.main
													} !important`
												}
											},
											"&.Mui-disabled": {
												backgroundColor: "#f5f5f5 !important",
												color: "#bdbdbd !important",
												cursor: "not-allowed !important",
												"&:hover": {
													backgroundColor: "#f5f5f5 !important"
												}
											}
										}
									}
								}}
								sx={datePickerStyles}
							/>
						</Grid2>

						{/* Current Position Checkbox */}
						<Grid2 size={{ xs: 12, md: 4, lg: 3 }}>
							<FormControlLabel
								control={
									<Switch
										checked={experience.isCurrentPosition}
										onChange={newValue => {
											// First update the checkbox state
											getCheckboxHandlers(
												`professionalExperiences.${index}.isCurrentPosition`
											).onChange(newValue);

											if (newValue) {
												// If current position is checked, clear the end date
												getDatePickerHandlers(
													`professionalExperiences.${index}.endDate`
												).onChange("");
											} else {
												// If current position is unchecked, trigger validation after state update
												setTimeout(() => {
													// Mark end date as touched and force validation
													formikRef.current.setFieldTouched(
														`professionalExperiences.${index}.endDate`,
														true,
														true // shouldValidate = true
													);
												}, 200);
											}
										}}
									/>
								}
								label={t(
									"freelancerOnboardingMyPresentation.experiences.currentPosition"
								)}
							/>
						</Grid2>

						{/* City */}
						<Grid2
							size={{ xs: 12, md: 6 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.city`, el)}
						>
							<PlacesAutocomplete
								fullWidth
								label={t("freelancerOnboardingMyPresentation.experiences.city")}
								name={`professionalExperiences.${index}.city`}
								value={
									experience.city
										? ({
												place_id: experience.city.id,
												formatted_address: experience.city.label
										  } as google.maps.places.PlaceResult)
										: null
								}
								onChange={(place: google.maps.places.PlaceResult | null) => {
									formik.setFieldValue(
										`professionalExperiences.${index}.city`,
										convertPlaceToCity(place)
									);

									setTimeout(() => {
										setIsCityValueChangedOnLoadBecauseOfBeingTranslated(
											place?.place_id ===
												formik.values.professionalExperiences?.[index].city?.id
										);
									}, 200);
								}}
								onBlur={() => {
									formikRef.current.setFieldTouched(
										`professionalExperiences.${index}.city`,
										true
									);
								}}
								converter={convertPlaceToCity}
								error={hasFieldError(`professionalExperiences.${index}.city`)}
								helperText={getFieldError(`professionalExperiences.${index}.city`)}
								placeholder={t(
									"freelancerOnboardingMyPresentation.experiences.cityPlaceholder"
								)}
								types={["(cities)"]}
								componentRestrictions={{ country: [] }}
							/>
						</Grid2>

						{/* Location */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Autocomplete
								options={jobLocations?.filter(x => x.label !== "NoPreference") || []}
								getOptionLabel={option => t(`JobLocations.${option.label}`)}
								value={experience.jobLocation}
								onChange={(_, value) =>
									formikRef.current.setFieldValue(
										`professionalExperiences.${index}.jobLocation`,
										value
									)
								}
								renderInput={params => (
									<TextField
										{...params}
										label={t("freelancerOnboardingMyPresentation.experiences.location")}
										placeholder={t(
											"freelancerOnboardingMyPresentation.experiences.selectLocation"
										)}
										fullWidth
									/>
								)}
							/>
						</Grid2>

						{/* Mission Description */}
						<Grid2
							size={{ xs: 12 }}
							ref={el =>
								setFieldRef(`professionalExperiences.${index}.missionDescription`, el)
							}
						>
							<TranslatableFieldWrapper
								label={t("freelancerOnboardingMyPresentation.experiences.mission")}
								{...translatableWrapperProps(
									"missionDescription",
									`professionalExperiences.${index}.missionDescription`
								)}
							>
								<HtmlEditor
									key={translatableForm.editingLanguage + "-missionDescription"}
									// Native lock (Tiptap editable): keeps internal scroll, selection/copy and
									// a11y alive on AI-managed content (the wrapper only dims it).
									editable={!translatableForm.isFieldReadOnly("missionDescription")}
									emitUpdatesOnlyWhenFocused
									description={t(
										"freelancerOnboardingMyPresentation.experiences.missionDescription"
									)}
									content={translatableForm.getFieldValue("missionDescription")}
									onUpdate={({ editor }) => {
										translatableForm.setFieldValue("missionDescription", editor.getHTML());
										if (translatableForm.editingLanguage === LanguageCode.Fr) {
											getDebouncedUpdate(
												`professionalExperiences.${index}.missionDescription`
											)(editor.getHTML());
										}
									}}
									onBlur={() => {
										formikRef.current.setFieldTouched(
											`professionalExperiences.${index}.missionDescription`,
											true
										);
									}}
								/>
							</TranslatableFieldWrapper>
							{hasFieldError(`professionalExperiences.${index}.missionDescription`) && (
								<Typography
									color="error"
									variant="body1"
									sx={{ mt: 1, display: "block" }}
								>
									{getFieldError(`professionalExperiences.${index}.missionDescription`)}
								</Typography>
							)}
						</Grid2>

						{/* Results */}
						<Grid2
							size={{ xs: 12 }}
							ref={el => setFieldRef(`professionalExperiences.${index}.results`, el)}
						>
							<TranslatableFieldWrapper
								label={t("freelancerOnboardingMyPresentation.experiences.results")}
								{...translatableWrapperProps(
									"results",
									`professionalExperiences.${index}.results`
								)}
							>
								<HtmlEditor
									key={translatableForm.editingLanguage + "-results"}
									editable={!translatableForm.isFieldReadOnly("results")}
									emitUpdatesOnlyWhenFocused
									description={t(
										"freelancerOnboardingMyPresentation.experiences.resultsDescription"
									)}
									content={translatableForm.getFieldValue("results")}
									onUpdate={({ editor }) => {
										translatableForm.setFieldValue("results", editor.getHTML());
										if (translatableForm.editingLanguage === LanguageCode.Fr) {
											getDebouncedUpdate(`professionalExperiences.${index}.results`)(
												editor.getHTML()
											);
										}
									}}
									onBlur={() => {
										formikRef.current.setFieldTouched(
											`professionalExperiences.${index}.results`,
											true
										);
									}}
								/>
							</TranslatableFieldWrapper>
							{hasFieldError(`professionalExperiences.${index}.results`) && (
								<Typography
									color="error"
									variant="body1"
									sx={{ mt: 1, display: "block" }}
								>
									{getFieldError(`professionalExperiences.${index}.results`)}
								</Typography>
							)}
						</Grid2>

						{/* Technical Stack */}
						<Grid2
							size={{ xs: 12 }}
							ref={el =>
								setFieldRef(`professionalExperiences.${index}.technicalStack`, el)
							}
						>
							<TranslatableFieldWrapper
								label={t("freelancerOnboardingMyPresentation.experiences.technicalStack")}
								{...translatableWrapperProps(
									"technicalStack",
									`professionalExperiences.${index}.technicalStack`
								)}
							>
								<HtmlEditor
									key={translatableForm.editingLanguage + "-technicalStack"}
									editable={!translatableForm.isFieldReadOnly("technicalStack")}
									emitUpdatesOnlyWhenFocused
									description={t(
										"freelancerOnboardingMyPresentation.experiences.technicalStackDescription"
									)}
									content={translatableForm.getFieldValue("technicalStack")}
									onUpdate={({ editor }) => {
										translatableForm.setFieldValue("technicalStack", editor.getHTML());
										if (translatableForm.editingLanguage === LanguageCode.Fr) {
											getDebouncedUpdate(`professionalExperiences.${index}.technicalStack`)(
												editor.getHTML()
											);
										}
									}}
									onBlur={() => {
										formikRef.current.setFieldTouched(
											`professionalExperiences.${index}.technicalStack`,
											true
										);
									}}
								/>
							</TranslatableFieldWrapper>
							{hasFieldError(`professionalExperiences.${index}.technicalStack`) && (
								<Typography
									color="error"
									variant="body1"
									sx={{ mt: 1, display: "block" }}
								>
									{getFieldError(`professionalExperiences.${index}.technicalStack`)}
								</Typography>
							)}
						</Grid2>

						{/* Skills and Domains */}
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
								{t("freelancerOnboardingMyPresentation.experiences.skillsAndDomains")}
							</Typography>
							<Typography variant="body2" gutterBottom>
								{t(
									"freelancerOnboardingMyPresentation.experiences.skillsAndDomainsDescription"
								)}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								isOptionEqualToValue={(option, value) => option.id === value.id}
								options={
									skills?.filter(x =>
										freelancerProfile.skills.some(y => y.skill.id === x.id)
									) || []
								}
								getOptionLabel={option => t(`Skills.${option.label}`)}
								value={experience.skills.map(x => x.skill)}
								onChange={(_, newValue) =>
									getAutocompleteHandlers(
										`professionalExperiences.${index}.skills`
									).onChange(
										_,
										newValue.map(
											(skill: Skill) =>
												({
													id: formikRef.current.values.professionalExperiences[
														index
													].skills.filter(x => x.skill.id === skill.id)?.[0]?.id,
													skill
												} as FreelancerProfileProfessionalExperienceSkill)
										)
									)
								}
								renderInput={params => (
									<TextField
										{...params}
										label={t("freelancerOnboardingMyPresentation.experiences.skills")}
										placeholder={t(
											"freelancerOnboardingMyPresentation.experiences.selectSkills"
										)}
										fullWidth
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								isOptionEqualToValue={(option, value) => option.id === value.id}
								options={
									businessDomains?.filter(x =>
										freelancerProfile.businessDomains.some(
											y => y.businessDomain.id === x.id
										)
									) || []
								}
								getOptionLabel={option => t(`BusinessDomains.${option.label}`)}
								value={experience.businessDomains.map(x => x.businessDomain)}
								onChange={(_, newValue) =>
									getAutocompleteHandlers(
										`professionalExperiences.${index}.businessDomains`
									).onChange(
										_,
										newValue.map(
											(businessDomain: BusinessDomain) =>
												({
													id: formikRef.current.values.professionalExperiences[
														index
													].businessDomains.filter(
														x => x.businessDomain.id === businessDomain.id
													)?.[0]?.id,
													businessDomain
												} as FreelancerProfileProfessionalExperienceBusinessDomain)
										)
									)
								}
								renderInput={params => (
									<TextField
										{...params}
										label={createTooltipLabel(
											"freelancerOnboardingMyPresentation.experiences.domains",
											"freelancerOnboardingMyPresentation.experiences.domainsTooltip"
										)}
										placeholder={t(
											"freelancerOnboardingMyPresentation.experiences.selectDomains"
										)}
										fullWidth
									/>
								)}
							/>
						</Grid2>

						{/* Documents and URLs Section */}
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="h4" gutterBottom sx={{ fontWeight: "bold" }}>
								{t("freelancerOnboardingMyPresentation.experiences.documents")}
							</Typography>
							<Typography variant="body2" gutterBottom>
								{t("freelancerOnboardingMyPresentation.experiences.documentsDescription")}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 5 }}>
							<Button
								startIcon={<LinkIcon />}
								onClick={addUrl}
								variant="outlined"
								size="large"
								disableRipple
								fullWidth
								sx={{
									color: zoullooColors.grey[700],
									borderColor: showingUrlTable
										? zoullooColors.success?.main || "#4caf50"
										: zoullooColors.grey[400],
									borderWidth: showingUrlTable ? 2 : 1,
									"&:hover": {
										borderColor: showingUrlTable
											? zoullooColors.success?.main || "#4caf50"
											: zoullooColors.grey[700],
										backgroundColor: zoullooColors.grey[100]
									}
								}}
							>
								{t("freelancerOnboardingMyPresentation.experiences.addUrl")}
							</Button>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 5 }}>
							<Button
								startIcon={<CloudUploadIcon />}
								onClick={addDocument}
								variant="outlined"
								size="large"
								disableRipple
								fullWidth
								sx={{
									color: zoullooColors.grey[700],
									borderColor:
										activeTable === "documents"
											? zoullooColors.success?.main || "#4caf50"
											: zoullooColors.grey[400],
									borderWidth: activeTable === "documents" ? 2 : 1,
									"&:hover": {
										borderColor:
											activeTable === "documents"
												? zoullooColors.success?.main || "#4caf50"
												: zoullooColors.grey[700],
										backgroundColor: zoullooColors.grey[100]
									}
								}}
							>
								{t("freelancerOnboardingMyPresentation.experiences.addDocument")}
							</Button>
						</Grid2>

						{/* Documents and URLs Tables */}
						<UrlsTable
							experienceIndex={index}
							experience={experience}
							formikRef={formikRef}
							hasFieldError={hasFieldError}
							getFieldError={getFieldError}
							isAddingUrl={isAddingUrl}
							addingUrl={addingUrl}
							setAddingUrl={setAddingUrl}
							setIsAddingUrl={setIsAddingUrl}
							showingUrlTable={showingUrlTable}
							setErrorDialog={setErrorDialog}
						/>
						<DocumentsTable
							experienceIndex={index}
							experience={experience}
							formikRef={formikRef}
							hasFieldError={hasFieldError}
							getFieldError={getFieldError}
							activeTable={activeTable}
							isAddingDocument={isAddingDocument}
							addingDocument={addingDocument}
							setAddingDocument={setAddingDocument}
							setIsAddingDocument={setIsAddingDocument}
							setErrorDialog={setErrorDialog}
						/>

						{/* Buttons Section */}
						<Grid2
							size={12}
							direction={{ xs: "column-reverse", md: "row-reverse" }}
							container
							spacing={3}
							marginTop={8}
						>
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Button
									size="medium"
									color="secondary"
									variant="outlined"
									fullWidth
									disabled={disableConfirmButtonOnPendingSave}
									onClick={handleCancel}
								>
									{t("freelancerOnboardingMyPresentation.experiences.button.cancel")}
								</Button>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<Button
									size="medium"
									color="secondary"
									variant="contained"
									fullWidth
									disabled={disableConfirmButtonOnPendingSave}
									onClick={() => handleConfirm(index)}
								>
									{t("freelancerOnboardingMyPresentation.experiences.button.confirm")}
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</CardContent>
			</Card>
		</LocalizationProvider>
	);
}
